import React, { useCallback } from "react";
import { Link } from "react-router-dom";
import type { AthleteHomeAction, AthleteHomeActionId } from "./athleteHomeActions";
import { markAthleteHomeActionVisited } from "./athleteHomeCompletion";
import { isExplorationHomeAction } from "../services/athleteHomeCompletionService";

export type AthleteHomeActionBlockProps = {
    action: AthleteHomeAction;
    destination: string;
    completed?: boolean;
    athleteProfileId?: number | null;
    onActionCompleted?: (actionId: AthleteHomeActionId) => void;
};

const AthleteHomeActionBlock: React.FC<AthleteHomeActionBlockProps> = React.memo(
    ({ action, destination, completed = false, athleteProfileId, onActionCompleted }) => {
        const handleClick = useCallback(() => {
            if (athleteProfileId == null || !isExplorationHomeAction(action.id)) {
                return;
            }
            void markAthleteHomeActionVisited(action.id)
                .then(() => {
                    onActionCompleted?.(action.id);
                })
                .catch(() => {
                    // Best-effort; home reload will reconcile.
                });
        }, [action.id, athleteProfileId, onActionCompleted]);

        const ctaLabel = completed ? "View" : action.ctaLabel;

        return (
            <article
                className={`athlete-home-action-block athlete-home-action-block--${action.accent}${completed ? " athlete-home-action-block--completed" : ""}`}
                data-testid={`athlete-home-block-${action.id}`}
                data-completed={completed ? "true" : "false"}
            >
                <div className="athlete-home-action-block__visual">
                    <img
                        src={action.image}
                        alt={action.imageAlt}
                        className="athlete-home-action-block__preview"
                        loading="lazy"
                        width={590}
                        height={360}
                    />
                </div>
                <div className="athlete-home-action-block__content">
                    <div className="athlete-home-action-block__heading-row">
                        <h2 className="athlete-home-action-block__title">{action.title}</h2>
                        {completed ? (
                            <span className="athlete-home-action-block__completed-badge" data-testid={`athlete-home-completed-${action.id}`}>
                                Completed
                            </span>
                        ) : null}
                    </div>
                    <p className="athlete-home-action-block__description">{action.description}</p>
                    <Link
                        to={destination}
                        className="athlete-home-action-block__cta"
                        data-testid={`athlete-home-cta-${action.id}`}
                        onClick={handleClick}
                    >
                        {ctaLabel}
                    </Link>
                </div>
            </article>
        );
    },
);

AthleteHomeActionBlock.displayName = "AthleteHomeActionBlock";

export default AthleteHomeActionBlock;
